import type { C1_Room } from "../model/C1_Room";
import type { C1_Opening } from "../model/C1_Opening";
import type { WallSide } from "../types/C1_ModelTypes";

export type C1_RoomNeighborContact = {
  side: WallSide;
  start: number;
  end: number;
  boundary: number;
  targetRoom: C1_Room;
};

/** Keeps the neighbor caption outside the envelope of a visible door swing. Distances are metres. */
export function roomNeighborLabelDistance(contact: C1_RoomNeighborContact, openings: C1_Opening[], name: string, px: number, wallThickness: number): number {
  let distance = 0.42;
  const verticalLabel = contact.side === "left" || contact.side === "right";
  const direction = contact.side === "left" || contact.side === "top" ? -1 : 1;
  const middle = (contact.start + contact.end) / 2;
  let textWidth = name.length * 7.2;
  if (typeof document !== "undefined") {
    const context = document.createElement("canvas").getContext("2d");
    if (context) {
      context.font = "400 12px Inter";
      textWidth = context.measureText(name).width;
    }
  }
  const halfLength = textWidth / (2 * px);
  const halfHeight = 6 / px;
  const x = verticalLabel ? contact.boundary + direction * distance : middle;
  const y = verticalLabel ? middle : contact.boundary + direction * distance;
  const halfX = verticalLabel ? halfHeight : halfLength;
  const halfY = verticalLabel ? halfLength : halfHeight;
  for (const opening of openings) {
    if (opening.kind !== "door" || opening.doorType === "passage" || opening.slidingDoor) continue;
    const center = opening.absolutePosition;
    const horizontal = opening.side === "top" || opening.side === "bottom";
    const inward = opening.side === "top" || opening.side === "left" ? 1 : -1;
    const along = horizontal ? center.x : center.y;
    const across = horizontal ? center.y : center.x;
    const end = across + inward * (opening.width + wallThickness / 2);
    const minAcross = Math.min(across, end) - wallThickness / 2;
    const maxAcross = Math.max(across, end) + wallThickness / 2;
    const minX = horizontal ? along - opening.width / 2 : minAcross;
    const maxX = horizontal ? along + opening.width / 2 : maxAcross;
    const minY = horizontal ? minAcross : along - opening.width / 2;
    const maxY = horizontal ? maxAcross : along + opening.width / 2;
    if (x + halfX >= minX && x - halfX <= maxX && y + halfY >= minY && y - halfY <= maxY) {
      distance = Math.max(distance, opening.width + 0.5);
    }
  }
  return distance;
}

/** Finds actual shared wall segments, including boundaries inside polygon bounding rectangles. */
export function roomNeighborContacts(candidate: C1_Room, rooms: C1_Room[]): C1_RoomNeighborContact[] {
  const tolerance = 0.001;
  const contacts: C1_RoomNeighborContact[] = [];
  for (const targetRoom of rooms) {
    for (const targetWall of targetRoom.walls ?? []) {
      const target = targetRoom.wallPoints(targetWall);
      if (!target) continue;
      const vertical = Math.abs(target.end.x - target.start.x) <= tolerance;
      const horizontal = Math.abs(target.end.y - target.start.y) <= tolerance;
      if (!vertical && !horizontal) continue;
      const boundary = vertical ? target.start.x : target.start.y;
      for (const candidateWall of candidate.walls ?? []) {
        const neighbor = candidate.wallPoints(candidateWall);
        if (!neighbor) continue;
        const firstBoundary = vertical ? neighbor.start.x : neighbor.start.y;
        const secondBoundary = vertical ? neighbor.end.x : neighbor.end.y;
        if (Math.abs(firstBoundary - boundary) > tolerance || Math.abs(secondBoundary - boundary) > tolerance) continue;
        const start = Math.max(
          Math.min(vertical ? target.start.y : target.start.x, vertical ? target.end.y : target.end.x),
          Math.min(vertical ? neighbor.start.y : neighbor.start.x, vertical ? neighbor.end.y : neighbor.end.x),
        );
        const end = Math.min(
          Math.max(vertical ? target.start.y : target.start.x, vertical ? target.end.y : target.end.x),
          Math.max(vertical ? neighbor.start.y : neighbor.start.x, vertical ? neighbor.end.y : neighbor.end.x),
        );
        if (end <= start + tolerance) continue;
        contacts.push({ side: targetRoom.compatibilitySide(targetWall), start, end, boundary, targetRoom });
      }
    }
  }
  return contacts;
}
